/**
 * Copyright (C) 2019 User X, LLC http://whoisuserx.com <info@userx.co>
 *
 * This program is the intellectual property of User X, LLC. You may
 * not redistribute and/or modify it without written consent by controlling
 * entities of User X, LLC.
 *
 * You should have received a copy of our Web Developer Agreement which
 * outlines proper use and distribution of this program. If you did not
 * please email info@userx.co to request your copy.
 */
/**
 * File created by John M. Woodcock < john@userx.co >
 * on 12/15/2025
 * for userx-support
 */

// javascript
import React, {useEffect, useState} from 'react';
import {useNavigate} from "react-router";
import useLocalStorage from "../../_hooks/useLocalStorage";

const WEBHOOK_URL = 'https://n8n.userx.co/webhook/agent-suggested-work';
const SUGGESTIONS_USER = process.env.REACT_APP_SUGGESTIONS_USER;
const SUGGESTIONS_PASS = process.env.REACT_APP_SUGGESTIONS_PASS;

const AgentSuggestedTickets = ({agentId, myAssigned, initialHours = 2}) => {
    const navigate = useNavigate();
    const [hours, setHours] = useState(initialHours);
    const [clientsOnly, setClientsOnly] = useState(false);
    const [tickets, setTickets] = useLocalStorage('uxAgentSuggestions', []);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!Array.isArray(myAssigned) || myAssigned.length === 0) return;
        if (!Array.isArray(tickets) || tickets.length === 0) return;

        // Map ticket_id => status_id for quick lookup (coerce to string/number for robustness)
        const assignedStatus = new Map(myAssigned.map(a => [String(a.ticket_id), Number(a.status_id)]));

        const filtered = tickets.filter(t => {
            const sid = assignedStatus.get(String(t.ticket_id));
            return sid === 2; // keep only tickets still "assigned"
        });

        // Only update storage if something changed
        if (filtered.length !== tickets.length) {
            setTickets(filtered);
        }
    }, [myAssigned, tickets, setTickets]);

    const fetchSuggestions = async () => {
        if (!agentId) {
            setError('Missing agent id');
            return;
        }

        // Guard missing creds early
        if (!SUGGESTIONS_USER || !SUGGESTIONS_PASS) {
            setError('Missing Basic auth credentials. Ensure `REACT_APP_SUGGESTIONS_USER` and `REACT_APP_SUGGESTIONS_PASS` are set at build time.');
            return;
        }

        setLoading(true);
        setError(null);
        setTickets([]);

        try {
            const basic = (typeof window !== 'undefined')
                ? btoa(`${SUGGESTIONS_USER}:${SUGGESTIONS_PASS}`)
                : Buffer.from(`${SUGGESTIONS_USER}:${SUGGESTIONS_PASS}`).toString('base64');

            const authHeader = `Basic ${basic}`;
            // Debug: verify header (remove in production)
            // console.log('Authorization header:', authHeader);

            const excludedCompanyIds = clientsOnly ? [1] : [];
            const reqData = {
                id: agentId,
                hours: hours,
                exclude: excludedCompanyIds
            };

            console.log('Request data for webhook:', reqData);

            const res = await fetch(WEBHOOK_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': authHeader
                },
                body: JSON.stringify(reqData)
            });

            if (!res.ok) {
                // If 403, capture body for debugging
                if (res.status === 403) {
                    const body = await res.text();
                    console.error('Webhook 403 response body:', body);
                }
                throw new Error(`Request failed: ${res.status}`);
            }

            const data = await res.json();
            if (!Array.isArray(data)) {
                throw new Error('Unexpected response format');
            }

            setTickets(data);
        } catch (err) {
            setError(err.message || 'Unknown error');
        } finally {
            setLoading(false);
        }
    };

    const totalEstimatedMinutes = tickets.reduce((sum, t) => sum + (Number(t.estimated_time) || 0), 0);
    const availableMinutes = hours * 60;

    return (
        <div className="agent-suggested-tickets wrap">
            <div style={{display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12}}>
                <label htmlFor="hours-select">Available hours:</label>
                <select
                    id="hours-select"
                    className={`me-3`}
                    value={hours}
                    onChange={(e) => setHours(Number(e.target.value))}
                >
                    {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((h) => (
                        <option key={h} value={h}>{h} hour{h !== 1 ? 's' : ''}</option>
                    ))}
                </select>

                <label htmlFor="clients-only"
                       className={`me-3`}>
                    <input
                        id="clients-only"
                        type="checkbox"
                        name="clients-only"
                        checked={clientsOnly}
                        onChange={(e) => setClientsOnly(e.target.checked)}
                    /> Clients Only
                </label>

                <button onClick={fetchSuggestions}
                    className={'btn btn-primary btn-sm'}
                        disabled={loading}>
                    {loading ? 'Loading...' : 'Get Suggested Tickets'}
                </button>
            </div>

            {error && <div style={{color: 'crimson', marginBottom: 12}}>Error: {error}</div>}

            {tickets.length === 0 && !loading && !error && (
                <div style={{color: '#666'}}>No suggestions yet. Choose hours and click "Get Suggested Tickets".</div>
            )}

            {tickets.length > 0 && (
                <div>
                    <div style={{marginBottom: 8}}>
                        Total estimated: {totalEstimatedMinutes} min — Available: {availableMinutes} min
                        {totalEstimatedMinutes > availableMinutes && (
                            <span style={{color: 'crimson', marginLeft: 8}}>(exceeds available hours)</span>
                        )}
                    </div>

                    <ul style={{listStyle: 'none', padding: 0, margin: 0}}>
                        {tickets.map((t) => (
                            <li key={t.ticket_id}
                                style={{cursor: 'pointer', padding: 10, borderBottom: '1px solid #eee'}}
                                onClick={() => navigate(`/clients/0/tickets/${t.ticket_id}`)}>
                                <div style={{fontWeight: 600}}>{t.title} <span
                                    style={{color: '#666', fontWeight: 400}}>#{t.ticket_id}</span></div>
                                <div style={{fontSize: 13, color: '#444'}}>Estimate: {t.estimated_time} min
                                </div>
                                {t.overdue_days != null &&
                                    <div style={{fontSize: 12, color: '#a00'}}>Overdue: {t.overdue_days} day(s)</div>
                                }
                                <div style={{marginTop: 6, fontSize: 13, color: '#333'}}>{t.reason}</div>
                            </li>
                        ))}
                    </ul>
                </div>
            )
            }
        </div>
    );
}

export default AgentSuggestedTickets;